import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { Archive, Pencil, Plus, RotateCcw, Users } from 'lucide-react'
import { useState, type ReactNode } from 'react'

import { PARTY_ROLE_VALUES, type PartySummary } from '@aeris/contracts'
import { partyDisplayName } from '@aeris/party'
import {
  Button,
  ColumnChooser,
  ConfirmDialog,
  EmptyState,
  InlineAlert,
  ListToolbar,
  Page,
  PageHeader,
  Pagination,
  QueryState,
  ResultCount,
  ActiveFilters,
  FilterableHeaderCell,
  RowActions,
  RowOpener,
  SearchInput,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
  VisuallyHidden,
  notify,
  type ActiveFilterChip,
  type ColumnFilterSpec,
  type ColumnFilterValue,
} from '@aeris/ui'

import { ExportMenu } from '../components/export-menu.tsx'
import {
  DEFAULT_PARTY_COLUMNS,
  PARTY_COLUMNS,
  partyIncludesFor,
  type PartySortKey,
} from '../components/party-columns.tsx'
import { PartyDetailDialog } from '../components/party-detail-dialog.tsx'
import { PartyForm } from '../components/party-form.tsx'
import { fetchSalespeople } from '../lib/api/team.ts'
import {
  archiveParty,
  fetchParties,
  fetchParty,
  restoreParty,
} from '../lib/api.ts'
import { useAuth } from '../lib/auth-context.ts'
import { enumValue, textValue } from '../lib/column-filters.ts'
import { partyExportColumns } from '../lib/party-export.ts'
import { PARTY_ROLE_LABELS } from '../lib/party-labels.ts'
import {
  availableColumns,
  shownColumnsOf,
  toggledColumns,
} from '../lib/table-columns.ts'
import { useDebounced } from '../lib/use-debounced.ts'
import { usePreference } from '../lib/use-preference.ts'
import { sortKey, useTableSort } from '../lib/use-table-sort.ts'
import { useWorkspace } from '../lib/workspace.ts'

/**
 * Pessoas — customers, suppliers, carriers and couriers in one list.
 *
 * There is deliberately no "Clientes" screen and no "Fornecedores" screen.
 * Master Specification section 10 keeps one registration carrying roles, and
 * two screens would invite two registrations for one CNPJ, which then drift
 * apart. The role filter is how somebody who only wants suppliers gets them.
 *
 * The same list pattern F007 established: server-side search, server-side
 * pagination, and a page size the client cannot raise past the server's cap.
 * Section 27 sets the baseline at 500.000 customers and forbids rendering
 * thousands of rows, so the list never holds more than one page in memory.
 *
 * Two permissions, not one. `party.create` shows "Nova pessoa"; `party.write`
 * shows the row actions. An operator at the counter registers the customer in
 * front of them and cannot rewrite an account that already exists.
 *
 * Gating only hides the buttons — the API
 * refuses the request independently, which is what actually enforces it.
 */

const PAGE_SIZE = 25

type RoleFilter = 'all' | (typeof PARTY_ROLE_VALUES)[number]

export const PessoasPage = (): ReactNode => {
  const { allows } = useAuth()
  const queryClient = useQueryClient()

  const [search, setSearch] = useState('')
  const [status, setStatus] = useState<'active' | 'archived'>('active')
  const [role, setRole] = useState<RoleFilter>('all')
  const [page, setPage] = useState(1)

  // The list goes back to the first page when the column changes: a new
  // order with the old offset shows rows that have nothing to do with
  // what was just clicked.
  const sort = useTableSort<PartySortKey>('legalName', 'asc', () => {
    setPage(1)
  })
  const [editing, setEditing] = useState<PartySummary | undefined>(undefined)
  const [formOpen, setFormOpen] = useState(false)

  /*
   * Chegando de fora com uma pessoa em mãos — F087.
   *
   * `/pessoas?editar=<id>` é o destino do marcador `+ Cidade` do atendimento.
   * A pessoa buscada por id e não procurada na lista: ela pode estar em
   * qualquer página, sob qualquer filtro, e o link precisa funcionar mesmo
   * quando a lista está mostrando outra coisa.
   */
  const { editar, voltar } = useSearch({ from: '/shell/pessoas' })
  const navigate = useNavigate()
  const workspace = useWorkspace()

  const deepLinked = useQuery({
    queryKey: ['party', editar],
    queryFn: async ({ signal }) => await fetchParty(editar ?? '', signal),
    enabled: editar !== undefined,
  })

  /*
   * DERIVADO, e não copiado para o estado por um efeito.
   *
   * Um `useEffect` que faz `setEditing` + `setFormOpen` assim que a consulta
   * responde é a armadilha que o `react-hooks/set-state-in-effect` acusa: dois
   * renders a mais por abertura, e duas fontes de verdade para "qual cadastro
   * está aberto" que só o cuidado de quem escreveu mantém em acordo. A URL já
   * é o estado; o resto se lê dela.
   *
   * Fechar o formulário é limpar o parâmetro — e é por isso que o `onClose`
   * navega em vez de mexer numa segunda variável.
   */
  const deepLinkedParty =
    editar === undefined ? undefined : deepLinked.data?.party
  const formParty = deepLinkedParty ?? editing
  const formVisible = formOpen || deepLinkedParty !== undefined

  // 300 ms: long enough that a typed word is one request, short enough that the
  // list feels attached to the keyboard.
  const debouncedSearch = useDebounced(search)

  const canCreate = allows('party.create')
  const canWrite = allows('party.write')

  /*
   * As colunas desta pessoa (F127), guardadas no servidor. As que moram fora
   * do cadastro (crédito, vale, última compra) só são pedidas quando estão na
   * tela: cada uma é uma consulta a mais.
   */
  const columnsPreference = usePreference('table.parties.columns', {
    visible: [...DEFAULT_PARTY_COLUMNS],
  })
  const offeredColumns = availableColumns(PARTY_COLUMNS, allows)
  const shownColumns = shownColumnsOf(
    offeredColumns,
    columnsPreference.value.visible
  )
  const includes = partyIncludesFor(shownColumns)

  /*
   * O filtro que está na tela, sem a página. A lista pede uma página dele; a
   * exportação (F131) pede todas — e as duas não podem discordar do filtro.
   */
  /** Os filtros de coluna (F152). */
  const [kindFilter, setKindFilter] = useState<'' | PartySummary['kind']>('')
  const [city, setCity] = useState('')
  /** A carteira (F210): um vendedor, `none`, ou vazio para todos. */
  const [salesperson, setSalesperson] = useState('')
  const salespeople = useQuery({
    queryKey: ['salespeople'],
    queryFn: async ({ signal }) => await fetchSalespeople(signal),
    enabled: allows('sales.write'),
  })

  const clearColumnFilters = (): void => {
    setKindFilter('')
    setCity('')
    setSalesperson('')
    setPage(1)
  }

  const columnFilters: Partial<
    Record<
      string,
      {
        filter: ColumnFilterSpec
        value?: ColumnFilterValue
        onFilter: (next: ColumnFilterValue | null) => void
      }
    >
  > = {
    kind: {
      filter: {
        kind: 'enum',
        options: [
          { value: 'person', label: 'Pessoa física' },
          { value: 'company', label: 'Empresa' },
        ],
      },
      ...(kindFilter === '' ? {} : { value: enumValue(kindFilter) }),
      onFilter: (next) => {
        setKindFilter(
          (next?.kind === 'enum' ? next.value : '') as '' | PartySummary['kind']
        )
        setPage(1)
      },
    },
    portfolio: {
      filter: {
        kind: 'enum',
        options: [
          { value: 'none', label: 'Sem vendedor' },
          ...(salespeople.data?.items ?? []).map((person) => ({
            value: person.userId,
            label: person.displayName,
          })),
        ],
      },
      ...(salesperson === '' ? {} : { value: enumValue(salesperson) }),
      onFilter: (next) => {
        setSalesperson(next?.kind === 'enum' ? next.value : '')
        setPage(1)
      },
    },
    city: {
      filter: { kind: 'text', placeholder: 'Ponta Grossa…' },
      ...(city === '' ? {} : { value: textValue(city) }),
      onFilter: (next) => {
        setCity(next?.kind === 'text' ? next.text : '')
        setPage(1)
      },
    },
  }

  const salespersonLabel =
    salesperson === 'none'
      ? 'Sem vendedor'
      : (salespeople.data?.items.find((person) => person.userId === salesperson)
          ?.displayName ?? 'vendedor')

  const chips: ActiveFilterChip[] = [
    ...(salesperson === ''
      ? []
      : [
          {
            id: 'portfolio',
            label: `Vendedor: ${salespersonLabel}`,
            onRemove: () => {
              setSalesperson('')
              setPage(1)
            },
          },
        ]),
    ...(kindFilter === ''
      ? []
      : [
          {
            id: 'kind',
            label: `Tipo: ${kindFilter === 'person' ? 'Pessoa física' : 'Empresa'}`,
            onRemove: () => {
              setKindFilter('')
              setPage(1)
            },
          },
        ]),
    ...(city === ''
      ? []
      : [
          {
            id: 'city',
            label: `Cidade: ${city}`,
            onRemove: () => {
              setCity('')
              setPage(1)
            },
          },
        ]),
  ]

  const listRequest = {
    ...(debouncedSearch.trim().length > 0
      ? { search: debouncedSearch.trim() }
      : {}),
    ...(role === 'all' ? {} : { role }),
    status,
    sort: sort.sort,
    direction: sort.direction,
    ...(includes.length === 0 ? {} : { include: includes }),
    ...(kindFilter === '' ? {} : { kind: kindFilter }),
    ...(city.trim() === '' ? {} : { city: city.trim() }),
    ...(salesperson === '' ? {} : { salesperson }),
  }

  const query = useQuery({
    queryKey: [
      'parties',
      {
        search: debouncedSearch,
        status,
        role,
        page,
        includes,
        // Sem o filtro na chave, o cache devolveria a lista sem ele.
        kindFilter,
        city,
        salesperson,
        ...sortKey(sort),
      },
    ],
    queryFn: ({ signal }) =>
      fetchParties({ ...listRequest, page, pageSize: PAGE_SIZE }, signal),
    // Keeps the previous page on screen while the next one loads, so the table
    // does not collapse to a spinner on every keystroke.
    placeholderData: (previous) => previous,
  })

  /* Arquivar pede confirmação e diz quando não deu (F132). */
  const [archiving, setArchiving] = useState<PartySummary | null>(null)
  const [rowError, setRowError] = useState<string | null>(null)

  const archive = useMutation({
    mutationFn: async (party: PartySummary) =>
      await archiveParty(party.id, party.version),
    onSuccess: async (_result, party) => {
      setRowError(null)
      // O retorno à vista (F248): a linha só sumia, sem dizer qual.
      notify.success(`Cadastro de ${partyDisplayName(party)} arquivado.`)
      await queryClient.invalidateQueries({ queryKey: ['parties'] })
    },
    onError: (_error, party) => {
      setRowError(
        `Não foi possível arquivar ${partyDisplayName(party)}. O cadastro pode ter sido alterado por outra pessoa; recarregue a lista e tente de novo.`
      )
    },
    onSettled: () => {
      setArchiving(null)
    },
  })

  const restore = useMutation({
    mutationFn: async (party: PartySummary) =>
      await restoreParty(party.id, party.version),
    onSuccess: async (_result, party) => {
      setRowError(null)
      notify.success(`Cadastro de ${partyDisplayName(party)} reativado.`)
      await queryClient.invalidateQueries({ queryKey: ['parties'] })
    },
    onError: (_error, party) => {
      setRowError(
        `Não foi possível reativar ${partyDisplayName(party)}. Tente de novo.`
      )
    },
  })

  const total = query.data?.total ?? 0
  const items = query.data?.items ?? []
  const filtered =
    debouncedSearch.trim().length > 0 ||
    role !== 'all' ||
    kindFilter !== '' ||
    city !== '' ||
    // A carteira também é filtro (F248): vazio nela não é "Nenhum cadastro".
    salesperson !== ''

  const openNew = (): void => {
    setEditing(undefined)
    setFormOpen(true)
  }

  const openEdit = (party: PartySummary): void => {
    setEditing(party)
    setFormOpen(true)
  }

  /** A ficha do cliente (F151): clicar na linha abre aqui, não na edição. */
  const [viewing, setViewing] = useState<PartySummary | null>(null)

  return (
    <Page>
      <PageHeader
        title="Clientes"
        description="Clientes, fornecedores, entregadores e transportadoras — um cadastro só, com papéis."
        actions={
          canCreate ? (
            <Button
              variant="primary"
              size="sm"
              onClick={openNew}
              leadingIcon={<Plus aria-hidden="true" className="h-4 w-4" />}
            >
              Novo cadastro
            </Button>
          ) : undefined
        }
      />

      <ListToolbar
        actions={
          <>
            <ColumnChooser
              columns={offeredColumns}
              visible={shownColumns.map((column) => column.id)}
              onToggle={(id) => {
                columnsPreference.save({
                  visible: toggledColumns(columnsPreference.value.visible, id),
                })
              }}
              onReset={() => {
                columnsPreference.save({ visible: [...DEFAULT_PARTY_COLUMNS] })
              }}
            />
            <ExportMenu
              fileName="clientes"
              sheetName="Clientes"
              columns={partyExportColumns(shownColumns)}
              fetchPage={(exportPage) =>
                fetchParties({
                  ...listRequest,
                  page: exportPage,
                  pageSize: 100,
                })
              }
              pageSize={100}
              total={query.data?.total ?? 0}
            />
          </>
        }
      >
        <SearchInput
          value={search}
          onChange={(event) => {
            setSearch(event.target.value)
            // Reset here rather than in an effect watching the debounced
            // value: a new search term always means page one, and doing it in
            // the handler avoids a render cascade.
            setPage(1)
          }}
          placeholder="Buscar por nome, documento (só números) ou o código do sistema antigo"
          aria-label="Buscar cadastros"
        />

        <Select
          value={role}
          onChange={(event) => {
            setRole(event.target.value as RoleFilter)
            setPage(1)
          }}
          aria-label="Papel"
          className="w-44"
        >
          <option value="all">Todos os papéis</option>
          {PARTY_ROLE_VALUES.map((value) => (
            <option key={value} value={value}>
              {PARTY_ROLE_LABELS[value]}
            </option>
          ))}
        </Select>

        <Select
          value={status}
          onChange={(event) => {
            setStatus(event.target.value as 'active' | 'archived')
            setPage(1)
          }}
          aria-label="Situação"
          className="w-44"
        >
          <option value="active">Ativos</option>
          <option value="archived">Arquivados</option>
        </Select>
      </ListToolbar>

      <ActiveFilters chips={chips} onClearAll={clearColumnFilters} />

      <ResultCount
        page={page}
        pageSize={PAGE_SIZE}
        total={total}
        loading={query.isPending}
        none="Nenhum cadastro encontrado"
      />

      {rowError === null ? null : (
        <InlineAlert tone="danger">{rowError}</InlineAlert>
      )}

      <QueryState
        isPending={query.isPending}
        isError={query.isError}
        error={query.error}
        onRetry={() => {
          void query.refetch()
        }}
        what="os cadastros"
        isEmpty={items.length === 0}
        filtered={filtered}
        onClearFilters={() => {
          setSearch('')
          setRole('all')
          clearColumnFilters()
        }}
        empty={
          <EmptyState
            icon={<Users aria-hidden="true" className="h-8 w-8" />}
            title={
              status === 'archived'
                ? 'Nenhum cadastro arquivado'
                : 'Nenhum cadastro ainda'
            }
            description={
              canCreate
                ? 'Cadastre o primeiro cliente, fornecedor ou entregador para começar.'
                : 'Você não tem permissão para cadastrar.'
            }
          />
        }
      >
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {shownColumns.map((column) => {
                  const filter = columnFilters[column.id]

                  if (column.sortKey === undefined && filter === undefined) {
                    return (
                      <TableHeaderCell
                        key={column.id}
                        className={column.headerClassName ?? ''}
                      >
                        {column.label}
                      </TableHeaderCell>
                    )
                  }

                  return (
                    <FilterableHeaderCell
                      key={column.id}
                      className={column.headerClassName ?? ''}
                      {...(column.sortKey === undefined
                        ? {}
                        : sort.column(column.sortKey))}
                      {...(filter ?? {})}
                    >
                      {column.label}
                    </FilterableHeaderCell>
                  )
                })}
                {/* Dropped entirely without `party.write`, rather than left
                    empty: a column with nothing under it reads as a broken
                    screen, not as a permission boundary. */}
                {canWrite ? (
                  <TableHeaderCell>
                    <VisuallyHidden>Ações</VisuallyHidden>
                  </TableHeaderCell>
                ) : null}
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((party) => (
                <TableRow
                  key={party.id}
                  onOpen={() => {
                    setViewing(party)
                  }}
                >
                  {shownColumns.map((column) => (
                    <TableCell
                      key={column.id}
                      numeric={column.numeric === true}
                      className={column.className ?? ''}
                    >
                      {column.id === 'name' ? (
                        <RowOpener
                          onOpen={() => {
                            setViewing(party)
                          }}
                        >
                          {column.cell(party)}
                        </RowOpener>
                      ) : (
                        column.cell(party)
                      )}
                    </TableCell>
                  ))}
                  {canWrite ? (
                    <TableCell>
                      <RowActions
                        name={party.legalName}
                        primary={
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              openEdit(party)
                            }}
                            leadingIcon={
                              <Pencil
                                aria-hidden="true"
                                className="h-3.5 w-3.5"
                              />
                            }
                          >
                            Editar
                          </Button>
                        }
                        items={[
                          party.status !== 'active' && {
                            label: 'Reativar',
                            icon: (
                              <RotateCcw
                                aria-hidden="true"
                                className="h-4 w-4"
                              />
                            ),
                            disabled: restore.isPending,
                            onSelect: () => {
                              restore.mutate(party)
                            },
                          },
                          party.status === 'active' && {
                            label: 'Arquivar',
                            danger: true,
                            icon: (
                              <Archive aria-hidden="true" className="h-4 w-4" />
                            ),
                            onSelect: () => {
                              setArchiving(party)
                            },
                          },
                        ]}
                      />
                    </TableCell>
                  ) : null}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </QueryState>

      <Pagination
        page={page}
        pageSize={PAGE_SIZE}
        total={total}
        onPageChange={setPage}
      />

      {viewing === null ? null : (
        <PartyDetailDialog
          party={viewing}
          onClose={() => {
            setViewing(null)
          }}
          {...(canWrite
            ? {
                onEdit: () => {
                  openEdit(viewing)
                  setViewing(null)
                },
              }
            : {})}
        />
      )}

      <ConfirmDialog
        open={archiving !== null}
        onOpenChange={(next) => {
          if (!next) setArchiving(null)
        }}
        title="Arquivar cadastro"
        description={
          archiving === null
            ? ''
            : `Arquivar ${archiving.legalName}? O cadastro some das buscas do PDV e das listas. Vendas, títulos e histórico continuam, e dá para reativar em Clientes → Arquivados.`
        }
        confirmLabel="Arquivar"
        loading={archive.isPending}
        onConfirm={() => {
          if (archiving !== null) archive.mutate(archiving)
        }}
      />

      {/* Mounted only while open, and keyed by the party being edited, so the
          form starts from the right values without an effect that resets them.
          React's own answer to "reset state when a prop changes". */}
      {formVisible ? (
        <PartyForm
          key={formParty?.id ?? 'new'}
          party={formParty}
          onClose={() => {
            setFormOpen(false)
            /*
             * Fechar devolve a pessoa de onde ela veio — F089.
             *
             * Quem clicou em "+ Cidade" no meio de um atendimento vinha com
             * `?voltar=atendimento`, e sem isto ficava parada em Pessoas,
             * longe da conversa que estava lendo. O caminho sai de
             * `routeFor`, nunca do texto da URL: `voltar` é uma CHAVE de
             * módulo validada, e não um endereço.
             *
             * Sem `voltar`, só limpa o parâmetro — senão o formulário
             * reabriria sozinho na próxima ida a Pessoas, e o botão Voltar do
             * navegador viraria uma armadilha.
             */
            if (voltar !== undefined) {
              void navigate({ to: workspace.routeFor(voltar) })
            } else if (editar !== undefined) {
              void navigate({ to: '/pessoas', search: {}, replace: true })
            }
          }}
        />
      ) : null}
    </Page>
  )
}
